import { type RefObject, useEffect, useId, useRef, useState } from 'react';
import { currentYearMonth } from '../../lib/businessDate';

const MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];
const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

function fromDateValue(value: string): Date {
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

function toDateValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function toMonthValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function formatMonthValue(value: string): string {
  const selected = fromDateValue(`${value}-01`);
  const month = MONTHS[selected.getMonth()];
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${selected.getFullYear()}`;
}

export function shiftMonthValue(value: string, offset: number): string {
  const selected = fromDateValue(`${value}-01`);
  return toMonthValue(new Date(selected.getFullYear(), selected.getMonth() + offset, 1));
}

type CalendarNavigationKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Home' | 'End';

function clampDateValue(value: string, min?: string, max?: string): string {
  if (min && value < min) return min;
  if (max && value > max) return max;
  return value;
}

function getInitialCalendarFocus(value: string, min?: string, max?: string): string {
  return clampDateValue(value || toDateValue(new Date()), min, max);
}

/** Calcula el siguiente día enfocado sin seleccionar ni cambiar el valor del campo. */
export function getCalendarKeyboardTarget(
  currentValue: string,
  key: string,
  min?: string,
  max?: string,
): string | null {
  const supportedKeys: ReadonlyArray<CalendarNavigationKey> = [
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ];
  if (!supportedKeys.includes(key as CalendarNavigationKey)) return null;

  const current = fromDateValue(currentValue);
  let offset = 0;
  switch (key as CalendarNavigationKey) {
    case 'ArrowLeft':
      offset = -1;
      break;
    case 'ArrowRight':
      offset = 1;
      break;
    case 'ArrowUp':
      offset = -7;
      break;
    case 'ArrowDown':
      offset = 7;
      break;
    case 'Home':
      offset = -((current.getDay() + 6) % 7);
      break;
    case 'End':
      offset = 6 - ((current.getDay() + 6) % 7);
      break;
  }

  const target = new Date(current.getFullYear(), current.getMonth(), current.getDate() + offset);
  return clampDateValue(toDateValue(target), min, max);
}

function CalendarIcon() {
  return (
    <svg
      className="h-[18px] w-[18px]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <path d="M3 10h18M8 3v4m8-4v4" />
    </svg>
  );
}

function Chevron({ direction }: { direction: 'left' | 'right' | 'down' }) {
  const path =
    direction === 'left'
      ? 'm14 6-6 6 6 6'
      : direction === 'right'
        ? 'm10 6 6 6-6 6'
        : 'm6 9 6 6 6-6';
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={path} />
    </svg>
  );
}

function useDismiss(
  open: boolean,
  onClose: () => void,
  refs: ReadonlyArray<RefObject<HTMLElement>>,
) {
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event: MouseEvent) => {
      if (!refs.some((ref) => ref.current?.contains(event.target as Node))) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Capturamos Escape antes del listener del Modal: al cerrar un
        // calendario abierto no debe cerrarse también todo el formulario.
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, onClose, refs]);
}

interface PopoverPosition {
  top: number;
  left: number;
  opensUp: boolean;
  maxHeight: number;
}

function getPopoverPosition(trigger: HTMLElement, width: number, height: number): PopoverPosition {
  const rect = trigger.getBoundingClientRect();
  const spaceAbove = Math.max(44, rect.top - 16);
  const spaceBelow = Math.max(44, window.innerHeight - rect.bottom - 16);
  const opensUp = spaceBelow < height && spaceAbove > spaceBelow;
  return {
    top: opensUp ? rect.top - 8 : rect.bottom + 8,
    left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
    opensUp,
    maxHeight: Math.min(height, opensUp ? spaceAbove : spaceBelow),
  };
}

interface DatePickerProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  min?: string;
  max?: string;
  helperText?: string;
  disabled?: boolean;
}

/** Nativo en móvil; calendario ligero y localizado en escritorio. */
export function DatePicker({
  id,
  label,
  value,
  onChange,
  required,
  min,
  max,
  helperText,
  disabled = false,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => fromDateValue(value));
  const [focusedDateValue, setFocusedDateValue] = useState(() =>
    getInitialCalendarFocus(value, min, max),
  );
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closePopover = () => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };
  useDismiss(open, closePopover, [rootRef, popoverRef]);

  useEffect(() => setView(fromDateValue(value)), [value]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const nextFocus = getInitialCalendarFocus(value, min, max);
    const nextDate = fromDateValue(nextFocus);
    setFocusedDateValue(nextFocus);
    setView(nextDate);
  }, [open, value, min, max]);

  useEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const updatePosition = () => {
      if (triggerRef.current) {
        setPopoverPosition(
          getPopoverPosition(
            triggerRef.current,
            popoverRef.current?.offsetWidth ?? 336,
            popoverRef.current?.scrollHeight ?? 456,
          ),
        );
      }
    };
    updatePosition();
    const measureFrame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.cancelAnimationFrame(measureFrame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, view]);

  useEffect(() => {
    if (!open) return undefined;
    const focusCalendar = window.requestAnimationFrame(() => {
      const exactDay = popoverRef.current?.querySelector<HTMLButtonElement>(
        `[data-calendar-date="${focusedDateValue}"]:not([disabled])`,
      );
      const fallbackDay = popoverRef.current?.querySelector<HTMLButtonElement>(
        '[data-calendar-date]:not([disabled])',
      );
      (exactDay ?? fallbackDay)?.focus();
    });
    return () => window.cancelAnimationFrame(focusCalendar);
  }, [open, focusedDateValue]);

  const selected = fromDateValue(value);
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const mondayIndex = (first.getDay() + 6) % 7;
  const start = new Date(view.getFullYear(), view.getMonth(), 1 - mondayIndex);
  const days = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
    return { date, value: toDateValue(date) };
  });
  const isOutsideRange = (dateValue: string) =>
    Boolean((min && dateValue < min) || (max && dateValue > max));
  const visibleFocusedDate =
    days.find(
      ({ value: dateValue }) => dateValue === focusedDateValue && !isOutsideRange(dateValue),
    )?.value ?? days.find(({ value: dateValue }) => !isOutsideRange(dateValue))?.value;

  return (
    <div className="relative" ref={rootRef}>
      <label
        id={`${id}-label`}
        htmlFor={`${id}-native`}
        className="mb-1.5 block text-sm font-semibold text-slate-700"
      >
        {label}
      </label>
      <input
        id={`${id}-native`}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        min={min}
        max={max}
        disabled={disabled}
        className="tap-target h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100 sm:hidden"
      />
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? closePopover() : setOpen(true))}
        disabled={disabled}
        className="hidden h-12 w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 text-left text-base text-slate-900 outline-none transition hover:border-slate-300 focus:border-slate-400 focus:ring-4 focus:ring-slate-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 sm:flex"
        aria-labelledby={`${id}-label ${id}-display`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={`${id}-calendar`}
      >
        <span id={`${id}-display`} className={value ? undefined : 'text-slate-500'}>
          {value
            ? new Intl.DateTimeFormat('es-MX', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              }).format(selected)
            : 'Selecciona una fecha'}
        </span>
        <span className="text-slate-500">
          <CalendarIcon />
        </span>
      </button>
      {open && popoverPosition && (
        <div
          ref={popoverRef}
          id={`${id}-calendar`}
          className="fixed z-[70] hidden w-[336px] rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_20px_40px_-16px_rgba(15,23,42,.3)] sm:block"
          style={{
            top: popoverPosition.top,
            left: popoverPosition.left,
            transform: popoverPosition.opensUp ? 'translateY(-100%)' : undefined,
            maxHeight: popoverPosition.maxHeight,
            overflowY: 'auto',
          }}
          role="dialog"
          aria-label={`Seleccionar ${label.toLowerCase()}`}
        >
          <div className="mb-3 flex items-center justify-between px-1">
            <button
              type="button"
              aria-label="Mes anterior"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
            >
              <Chevron direction="left" />
            </button>
            <span className="text-sm font-bold capitalize text-slate-900" aria-live="polite">
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </span>
            <button
              type="button"
              aria-label="Mes siguiente"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
            >
              <Chevron direction="right" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-y-1 text-center text-xs font-semibold text-slate-600">
            {WEEKDAYS.map((day, index) => (
              <span key={`${day}-${index}`} className="py-1">
                {day}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-y-1">
            {days.map(({ date, value: dateValue }) => {
              const active = dateValue === value;
              const muted = date.getMonth() !== view.getMonth();
              const disabled = isOutsideRange(dateValue);
              return (
                <button
                  key={dateValue}
                  type="button"
                  disabled={disabled}
                  data-calendar-date={dateValue}
                  data-form-value-change={active ? undefined : 'true'}
                  data-selected={active ? 'true' : undefined}
                  tabIndex={dateValue === visibleFocusedDate ? 0 : -1}
                  aria-label={new Intl.DateTimeFormat('es-MX', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  }).format(date)}
                  aria-pressed={active}
                  aria-current={dateValue === toDateValue(new Date()) ? 'date' : undefined}
                  onFocus={() => setFocusedDateValue(dateValue)}
                  onKeyDown={(event) => {
                    const targetValue = getCalendarKeyboardTarget(dateValue, event.key, min, max);
                    if (!targetValue) return;

                    event.preventDefault();
                    const targetDate = fromDateValue(targetValue);
                    if (
                      targetDate.getMonth() !== view.getMonth() ||
                      targetDate.getFullYear() !== view.getFullYear()
                    ) {
                      setView(targetDate);
                    }
                    setFocusedDateValue(targetValue);
                  }}
                  onClick={() => {
                    onChange(dateValue);
                    closePopover();
                  }}
                  className={`mx-auto flex h-8 w-8 items-center justify-center rounded-lg text-xs transition ${active ? 'bg-slate-900 font-bold text-white shadow-sm' : muted ? 'text-slate-600 hover:bg-slate-50' : 'text-slate-700 hover:bg-slate-100'} disabled:cursor-not-allowed disabled:opacity-30`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-form-value-change={toDateValue(new Date()) === value ? undefined : 'true'}
                onClick={() => {
                  const today = new Date();
                  setView(today);
                  onChange(toDateValue(today));
                  closePopover();
                }}
                className="tap-target text-xs font-semibold text-savings hover:text-blue-700"
              >
                Hoy
              </button>
              {!required && value && (
                <button
                  type="button"
                  data-form-value-change="true"
                  onClick={() => {
                    onChange('');
                    closePopover();
                  }}
                  className="tap-target text-xs font-semibold text-slate-600 hover:text-slate-900"
                >
                  Quitar fecha
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={closePopover}
              className="tap-target text-xs font-semibold text-slate-600 hover:text-slate-900"
            >
              Listo
            </button>
          </div>
        </div>
      )}
      {helperText && <p className="mt-1.5 text-xs leading-4 text-slate-500">{helperText}</p>}
    </div>
  );
}

interface MonthPickerProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  controlClassName?: string;
  required?: boolean;
  min?: string;
  max?: string;
  helperText?: string;
  allTime?: boolean;
  onAllTime?: () => void;
}

/** Selector localizado, consistente entre móvil y escritorio y seguro dentro de modales. */
export function MonthPicker({
  id,
  label,
  value,
  onChange,
  className = 'w-full sm:w-[280px] sm:max-w-full',
  controlClassName = '',
  required,
  min,
  max,
  helperText,
  allTime = false,
  onAllTime,
}: MonthPickerProps) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => fromDateValue(`${value}-01`).getFullYear());
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closePopover = () => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };
  useDismiss(open, closePopover, [rootRef, popoverRef]);
  const isOutsideRange = (monthValue: string) =>
    Boolean((min && monthValue < min) || (max && monthValue > max));
  const previousMonth = shiftMonthValue(value, -1);
  const nextMonth = shiftMonthValue(value, 1);
  const currentMonth = currentYearMonth();
  const selectedLabel = formatMonthValue(value);

  useEffect(() => setYear(fromDateValue(`${value}-01`).getFullYear()), [value]);

  useEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const updatePosition = () => {
      if (triggerRef.current) {
        setPopoverPosition(
          getPopoverPosition(
            triggerRef.current,
            popoverRef.current?.offsetWidth ?? 280,
            popoverRef.current?.scrollHeight ?? (onAllTime ? 376 : 324),
          ),
        );
      }
    };
    updatePosition();
    const measureFrame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.cancelAnimationFrame(measureFrame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, onAllTime]);

  useEffect(() => {
    if (!open) return undefined;
    const focusPicker = window.requestAnimationFrame(() => {
      const selected =
        popoverRef.current?.querySelector<HTMLButtonElement>('[data-selected="true"]');
      const fallback =
        popoverRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])');
      (selected ?? fallback)?.focus();
    });
    return () => window.cancelAnimationFrame(focusPicker);
  }, [open]);

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {label && (
        <label
          htmlFor={`${inputId}-trigger`}
          className="mb-1.5 block text-sm font-semibold text-slate-700"
        >
          {label}
        </label>
      )}
      <div className="grid grid-cols-[44px_minmax(0,1fr)_44px] items-stretch gap-1.5">
        <button
          type="button"
          data-form-value-change="true"
          disabled={isOutsideRange(previousMonth)}
          onClick={() => onChange(previousMonth)}
          className="tap-target flex min-h-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={`Mes anterior: ${formatMonthValue(previousMonth)}`}
        >
          <Chevron direction="left" />
        </button>
        <button
          ref={triggerRef}
          id={`${inputId}-trigger`}
          type="button"
          onClick={() => (open ? closePopover() : setOpen(true))}
          className={`${controlClassName} tap-target flex min-h-11 min-w-0 items-center justify-between gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:border-slate-300 focus:border-slate-400 focus:ring-4 focus:ring-slate-100`}
          aria-label={`${label ?? 'Mes de consulta'}: ${allTime ? 'Todo el historial' : selectedLabel}`}
          aria-required={required || undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={`${inputId}-calendar`}
          aria-describedby={helperText ? `${inputId}-helper` : undefined}
        >
          <span className="min-w-0 truncate">{allTime ? 'Todo el historial' : selectedLabel}</span>
          <CalendarIcon />
        </button>
        <button
          type="button"
          data-form-value-change="true"
          disabled={isOutsideRange(nextMonth)}
          onClick={() => onChange(nextMonth)}
          className="tap-target flex min-h-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={`Mes siguiente: ${formatMonthValue(nextMonth)}`}
        >
          <Chevron direction="right" />
        </button>
      </div>
      {open && popoverPosition && (
        <div
          ref={popoverRef}
          id={`${inputId}-calendar`}
          className="fixed z-[70] w-[280px] rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_20px_40px_-16px_rgba(15,23,42,.3)]"
          style={{
            top: popoverPosition.top,
            left: popoverPosition.left,
            transform: popoverPosition.opensUp ? 'translateY(-100%)' : undefined,
            maxHeight: popoverPosition.maxHeight,
            overflowY: 'auto',
          }}
          role="dialog"
          aria-label={label ? `Seleccionar ${label.toLowerCase()}` : 'Seleccionar mes'}
        >
          <div className="mb-3 flex items-center justify-between px-1">
            <button
              type="button"
              aria-label="Año anterior"
              onClick={() => setYear((current) => current - 1)}
              className="tap-target flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
            >
              <Chevron direction="left" />
            </button>
            <span className="text-sm font-bold text-slate-900">{year}</span>
            <button
              type="button"
              aria-label="Año siguiente"
              onClick={() => setYear((current) => current + 1)}
              className="tap-target flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
            >
              <Chevron direction="right" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {MONTHS.map((name, index) => {
              const monthValue = `${year}-${String(index + 1).padStart(2, '0')}`;
              const active = !allTime && monthValue === value;
              const disabled = isOutsideRange(monthValue);
              return (
                <button
                  key={name}
                  type="button"
                  disabled={disabled}
                  data-form-value-change={active ? undefined : 'true'}
                  data-selected={active ? 'true' : undefined}
                  aria-pressed={active}
                  onClick={() => {
                    onChange(monthValue);
                    closePopover();
                  }}
                  className={`tap-target min-h-11 rounded-lg text-xs font-semibold capitalize transition ${active ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'} disabled:cursor-not-allowed disabled:opacity-30`}
                >
                  {name.slice(0, 3)}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            data-form-value-change={currentMonth === value && !allTime ? undefined : 'true'}
            disabled={isOutsideRange(currentMonth)}
            onClick={() => {
              onChange(currentMonth);
              setYear(Number(currentMonth.slice(0, 4)));
              closePopover();
            }}
            className="mt-3 min-h-11 w-full border-t border-slate-100 pt-3 text-xs font-semibold text-savings hover:text-blue-700 disabled:cursor-not-allowed disabled:text-slate-400"
          >
            Mes actual
          </button>
          {onAllTime && (
            <button
              type="button"
              data-form-value-change={allTime ? undefined : 'true'}
              data-selected={allTime ? 'true' : undefined}
              aria-pressed={allTime}
              onClick={() => {
                onAllTime();
                closePopover();
              }}
              className={`mt-2 w-full rounded-lg py-2 text-xs font-semibold transition ${allTime ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
            >
              Todo el historial
            </button>
          )}
        </div>
      )}
      {helperText && (
        <p id={`${inputId}-helper`} className="mt-1.5 text-xs leading-4 text-slate-500">
          {helperText}
        </p>
      )}
    </div>
  );
}
